import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../auth";
import ListLayout from "../../../components/Layout/ListLayout";
import { useListCovers, useCancelSignatureBatchById } from "../../../api/documentos/endpoints";
import {
  EnvelopeFilters,
  EnvelopeFiltersData,
} from "./components/EnvelopeFilters";
import { EnvelopeCards } from "./components/EnvelopeCards";
import { CoverDto } from "../../../api/documentos/model/coverDto";
import { ListCoversParams } from "../../../api/documentos/model/listCoversParams";
import React from "react";

const envelopesSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  title: z.string().optional(),
  createdAt: z.string().optional(),
  signatureDueDate: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute("/__auth/signatures/envelopes")({
  component: EnvelopesListPage,
  validateSearch: (search) => envelopesSearchSchema.parse(search),
  staticData: {
    title: "routes.envelopes",
  },
});

function EnvelopeActionsCell({ row, onCancel }: { row: CoverDto; onCancel: (envelope: CoverDto) => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/signatures/${row.id}/envelope` });
          }}
        >
          <ListItemIcon>
            <EmailIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("signatures.details")}</ListItemText>
        </MenuItem>
        {row.status !== "CANCELLED" && row.status !== "CANCELED" && row.status !== "SIGNED" && row.status !== "COMPLETED" && row.status !== "EXPIRED" && (
          <MenuItem
            onClick={() => {
              handleClose();
              onCancel(row);
            }}
          >
            <ListItemIcon>
              <CancelIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>{t("common.cancel")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

interface CancelSignatureDialogProps {
  open: boolean;
  envelope: CoverDto;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

function CancelSignatureDialog({
  open,
  envelope,
  onClose,
  onSuccess,
  onError,
}: CancelSignatureDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tenantId = user?.tenant || "default-domain";
  const [reason, setReason] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const cancelMutation = useCancelSignatureBatchById();

  const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setReason(val);
    if (!val.trim()) {
      setErrorMsg(t("signatures.cancelReasonRequired") || "O motivo do cancelamento é obrigatório");
    } else if (val.length > 1000) {
      setErrorMsg(t("signatures.cancelReasonMaxLength") || "O motivo deve ter no máximo 1000 caracteres");
    } else {
      setErrorMsg("");
    }
  };

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setErrorMsg(t("signatures.cancelReasonRequired") || "O motivo do cancelamento é obrigatório");
      return;
    }
    if (trimmedReason.length > 1000) {
      setErrorMsg(t("signatures.cancelReasonMaxLength") || "O motivo deve ter no máximo 1000 caracteres");
      return;
    }

    cancelMutation.mutate(
      {
        tenantId,
        coverId: envelope.id!,
        data: {
          cancellationReason: trimmedReason,
        },
      },
      {
        onSuccess: () => {
          onSuccess();
        },
        onError: (err: any) => {
          const errMsg = err?.response?.data?.message || err?.message || t("signatures.cancelError") || "Erro ao cancelar o envelope";
          onError(errMsg);
        },
      }
    );
  };

  const isSubmitDisabled = !reason.trim() || reason.length > 1000 || cancelMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("signatures.cancelEnvelopeTitle") || "Cancelar Envelope"}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("signatures.cancelEnvelopeConfirm") || "Deseja realmente cancelar este envelope de assinatura?"}
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label={t("signatures.cancelReasonLabel") || "Motivo do cancelamento"}
          type="text"
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          value={reason}
          onChange={handleReasonChange}
          error={!!errorMsg}
          helperText={errorMsg || `${reason.length}/1000`}
          disabled={cancelMutation.isPending}
          inputProps={{ maxLength: 1000 }}
        />
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button onClick={onClose} color="inherit" disabled={cancelMutation.isPending}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isSubmitDisabled}
          startIcon={cancelMutation.isPending ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {t("common.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EnvelopesListPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const navigateGeneric = useNavigate();
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";

  const [cancelEnvelope, setCancelEnvelope] = useState<CoverDto | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const {
    data: coversDataResponse,
    isLoading,
    error,
    refetch,
  } = useListCovers(tenant, {
    page: search.page - 1,
    size: search.size,
    sort: ["id,asc"],
    title: search.title || undefined,
    createdAt: search.createdAt ? `${search.createdAt}T00:00:00Z` : undefined,
    signatureDueDate: search.signatureDueDate ? `${search.signatureDueDate}T23:59:59Z` : undefined,
    status: search.status || undefined,
  } as unknown as ListCoversParams);

  const data = coversDataResponse as any;

  const handleFilter = (filters: EnvelopeFiltersData) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...filters,
        page: 1,
      }),
    });
  };

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: newPage + 1,
      }),
    });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    navigate({
      search: (prev) => ({
        ...prev,
        size: parseInt(event.target.value, 10),
        page: 1,
      }),
    });
  };

  const columnHelper = createColumnHelper<CoverDto>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("title", {
        header: t("signatures.envelopeTitle"),
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue() || "-"}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("authorName", {
        header: t("signatures.createdBy"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("createdAt", {
        header: t("common.creationDate"),
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue() as string).toLocaleString("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
              })
            : "-",
      }),
      columnHelper.accessor("signatureDueDate", {
        header: t("signatures.expirationDate"),
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue() as string).toLocaleDateString("pt-BR")
            : "-",
      }),
      columnHelper.accessor("status", {
        header: t("signatures.situation"),
        cell: (info) => {
          const status = info.getValue() as string;
          let label = t("signatures.status.pending");
          let color: "default" | "success" | "error" | "warning" | "info" =
            "default";

          switch (status) {
            case "COMPLETED":
            case "SIGNED":
              label = t("signatures.status.signed");
              color = "success";
              break;
            case "SIGNATURE_STARTED":
            case "PROCESSING":
              label = t("signatures.status.inProgress");
              color = "info";
              break;
            case "CANCELED":
            case "CANCELLED":
              label = t("signatures.status.canceled");
              color = "error";
              break;
            case "REJECTED":
              label = t("signatures.status.rejected");
              color = "error";
              break;
            case "EXPIRED":
              label = t("signatures.status.expired");
              color = "error";
              break;
            case "PENDING":
            default:
              label = t("signatures.status.pending");
              color = "warning";
              break;
          }

          return <Chip label={label} color={color} size="small" />;
        },
      }),
      columnHelper.display({
        id: "actions",
        header: t("signatures.actions"),
        cell: (info) => <EnvelopeActionsCell row={info.row.original} onCancel={(env) => setCancelEnvelope(env)} />,
        meta: { align: "right", isSticky: true },
      }),
    ],
    [columnHelper, t, setCancelEnvelope],
  );

  const table = useReactTable({
    data: data?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: data?.totalPages || -1,
  });

  return (
    <ListLayout
      title={t("signatures.envelopes")}
      subtitle={t("signatures.manageEnvelopes")}
      isLoading={isLoading}
      error={error}
      isEmpty={!data?.content?.length}
      filters={
        <EnvelopeFilters
          onFilter={handleFilter}
          onClear={handleClearFilters}
          isLoading={isLoading}
          initialValues={search}
        />
      }
    >
      {isMobile ? (
        <EnvelopeCards
          envelopes={data?.content || []}
          renderActions={(envelope, closeMenu) => (
            <>
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateGeneric({ to: `/signatures/${envelope.id}/envelope` });
                }}
              >
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t("signatures.details")}</ListItemText>
              </MenuItem>
              {envelope.status !== "CANCELLED" && envelope.status !== "CANCELED" && envelope.status !== "SIGNED" && envelope.status !== "COMPLETED" && envelope.status !== "EXPIRED" && (
                <MenuItem
                  onClick={() => {
                    closeMenu();
                    setCancelEnvelope(envelope);
                  }}
                >
                  <ListItemIcon>
                    <CancelIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: "error.main" }}>{t("common.cancel")}</ListItemText>
                </MenuItem>
              )}
            </>
          )}
        />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "background.default" }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta: any = header.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "background.default",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta: any = cell.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "inherit",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={data?.totalElements || 0}
        page={(search.page || 1) - 1}
        onPageChange={handlePageChange}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.moreThan")} ${to}`}`
        }
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />
      {cancelEnvelope && (
        <CancelSignatureDialog
          open={!!cancelEnvelope}
          envelope={cancelEnvelope}
          onClose={() => setCancelEnvelope(null)}
          onSuccess={() => {
            setCancelEnvelope(null);
            showSnackbar(t("signatures.cancelSuccess") || "Envelope cancelado com sucesso", "success");
            refetch();
          }}
          onError={(msg) => {
            showSnackbar(msg, "error");
          }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
